import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { Button, CircularProgress, Box } from "@mui/material";
import { APIBilling } from "../../../../../api";
import { DATES_VALUE } from "../../../../../constants";
import { DetailedSalesReportStrategy } from "./detailedSalesReportStrategy";
import { BasicSalesReportStrategy } from "./basicSalesReportStrategy";
import { BILLING_API_URLS } from "../../../../../api/billing";
import { financialsActions } from "../../../../../reducers/financials";
import { SelectByDateWithCustomPeriod } from "../../../../../components/ui";
import { ReportExporter } from "./reportBuilder";
import downloadCsv from "../../../../../utils/downloadCsv";

interface IProps {
  isIgnoreWeekends: boolean;
  broker?: string;
}

const ExportCsvProductsStats: React.FC<IProps> = ({ isIgnoreWeekends, broker }) => {
  const dispatch = useDispatch();

  const { data: report } = useSelector((state: RootState) => state.financials.sales_by_days);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isAllTime, setIsAllTime] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const getReport = async (startDate: string, endDate: string, isAllTime?: boolean) => {
    setStartDate(startDate);
    setEndDate(endDate);
    setIsAllTime(!!isAllTime);
  };

  const fetchData = useQuery({
    enabled: !!startDate || !!endDate,
    queryKey: [BILLING_API_URLS.getSalesStatsByDay, startDate, endDate, isAllTime, broker],
    async queryFn({ signal }) {
      try {
        dispatch(financialsActions.fetchSalesByDaysRequest());
        const query = new URLSearchParams();
        query.append("startDate", startDate);
        query.append("endDate", endDate);
        query.append("isAllTime", String(isAllTime));
        if (broker) query.append("broker", broker);

        const response = await APIBilling.getSalesStatsByDayRequest(query.toString(), signal);
        dispatch(financialsActions.fetchSalesByDaysSuccess(response.data.report));
        return response;
      } catch (error) {
        dispatch(financialsActions.fetchSalesByDaysFailed());
      }
    },
  });

  const buildBaseQuery = () => {
    const query = new URLSearchParams();
    query.append("startDate", startDate);
    query.append("endDate", endDate);
    query.append("isAllTime", String(isAllTime));
    return query;
  };

  const downloadDetailedReport = () => {
    const reportData = new DetailedSalesReportStrategy(report, isIgnoreWeekends);
    const reportBuilder = new ReportExporter(
      reportData,
      {
        startDate,
        endDate,
        isAllTime,
      },
      downloadCsv
    );
    reportBuilder.downloadReport("detailed-report-sales");
  };

  const downloadBasicReport = async () => {
    setIsDownloading(true);
    try {
      const tradovateQuery = buildBaseQuery();
      tradovateQuery.append("broker", "tradovate");
      const rithmicQuery = buildBaseQuery();
      rithmicQuery.append("broker", "rithmic");

      const [tradovateRes, rithmicRes] = await Promise.all([
        APIBilling.getSalesStatsByDayRequest(tradovateQuery.toString(), undefined),
        APIBilling.getSalesStatsByDayRequest(rithmicQuery.toString(), undefined),
      ]);

      const reportData = new BasicSalesReportStrategy(
        report,
        isIgnoreWeekends,
        tradovateRes.data.report,
        rithmicRes.data.report
      );
      const reportBuilder = new ReportExporter(
        reportData,
        { startDate, endDate, isAllTime },
        downloadCsv
      );
      reportBuilder.downloadReport("basic-report-sales");
    } finally {
      setIsDownloading(false);
    }
  };

  const isLoading = fetchData.isLoading || fetchData.isFetching;
  return (
    <Box>
      <Box sx={{ width: "fit-content", display: "flex", flexDirection: "row" }}>
        <SelectByDateWithCustomPeriod
          setIsLoading={() => void null}
          getReport={getReport}
          setReport={() => void null}
          initialDateBy={DATES_VALUE.thisWeek}
        />

        <Button
          sx={{ ml: "10px", minWidth: "160px" }}
          disabled={isLoading}
          endIcon={isLoading && <CircularProgress size={20} />}
          onClick={downloadDetailedReport}
        >
          Download detailed CSV report
        </Button>

        <Button
          sx={{ ml: "10px", minWidth: "160px" }}
          disabled={isLoading || isDownloading}
          endIcon={(isLoading || isDownloading) && <CircularProgress size={20} />}
          onClick={downloadBasicReport}
        >
          Download basic CSV report
        </Button>
      </Box>
    </Box>
  );
};

export default ExportCsvProductsStats;
